import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ANNIVERSARY_PRESET_TITLE, type Visibility } from "@wodiary/shared";
import { api } from "../api/client.js";
import {
  useAnniversaries,
  useAnniversaryEntries,
  usePairing,
} from "../api/queries.js";
import { useAuth } from "../auth/AuthContext.js";
import { errorMessage } from "../lib/errors.js";
import {
  anniversaryCountdown,
  countdownLabel,
  formatAnniversaryDate,
  formatTime,
  monthDayBadge,
} from "../lib/format.js";
import type { AnniversaryView } from "../api/types.js";
import { NavBar } from "../components/NavBar.js";
import { Empty } from "../components/Empty.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { Modal } from "../components/Modal.js";
import { useToast } from "../components/ToastContext.js";
import { CalendarIcon, HeartIcon, LockIcon, PlusIcon } from "../components/icons.js";

/**
 * 纪念日（spec §5.5 `ANNI-1`）。
 *
 * 每一条自带 `visibility`：`private` 的对伴侣**不存在**（不显示、不计入倒计时、
 * 不发提醒）。列表里因此不会出现对方的私密纪念日 —— 那不是「显示为灰色」，
 * 是根本不在数据里。
 *
 * 「跳到那天的日记」的目标集合 = 当天双方**共享日记**的并集（后端保证），
 * 若为空，表现与「那天没写日记」逐字一致。
 */
export function AnniversariesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const list = useAnniversaries();
  const pairing = usePairing();
  const paired = pairing.data?.state === "active";

  const [editing, setEditing] = useState<AnniversaryView | null>(null);
  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ["anniversaries"] });

  const items = useMemo(() => {
    const list0 = list.data?.anniversaries ?? [];
    return [...list0].sort(
      (a, b) =>
        anniversaryCountdown(a.date).days - anniversaryCountdown(b.date).days,
    );
  }, [list.data]);

  if (user === null) return null;

  return (
    <>
      <NavBar
        title="纪念日"
        right={
          <button
            type="button"
            className="icon-btn"
            aria-label="新增纪念日"
            disabled={!paired}
            onClick={() => {
              setError(null);
              setCreating(true);
            }}
          >
            <PlusIcon size={20} />
          </button>
        }
      />
      <div className="page">
        {!paired && (
          <div className="banner banner-warn">
            <LockIcon size={16} />
            <span>配对之后才能创建纪念日。</span>
          </div>
        )}

        {list.isLoading && <Loading />}
        {list.isError && (
          <QueryError error={list.error} onRetry={() => void list.refetch()} />
        )}

        {list.data !== undefined && items.length === 0 && (
          <Empty
            icon={<HeartIcon size={22} style={{ color: "var(--steel)" }} />}
            title="还没有纪念日"
            description="记下在一起的日子、生日、以及任何你们想记住的日期。每条可以选择只给自己看，或让 TA 也看到。"
            action={
              paired ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setCreating(true)}
                >
                  添加第一条
                </button>
              ) : undefined
            }
          />
        )}

        {items.length > 0 && (
          <div className="stack">
            {items.map((a) => (
              <AnniversaryItem
                key={a.id}
                anniversary={a}
                meId={user.id}
                partner={pairing.data?.partnerUsername ?? "TA"}
                expanded={expanded === a.id}
                onToggle={() =>
                  setExpanded((cur) => (cur === a.id ? null : a.id))
                }
                onEdit={() => {
                  setError(null);
                  setEditing(a);
                }}
              />
            ))}
          </div>
        )}

        <p className="foot-note">
          「配对日」是系统记录的，和「在一起纪念日」是两件事 —— 产品不会提示
          两者是否一致，那是你们的日期。
        </p>
      </div>

      {(creating || editing !== null) && (
        <AnniversaryForm
          anniversary={editing}
          partner={pairing.data?.partnerUsername ?? "TA"}
          error={error}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
            toast.show(editing === null ? "纪念日已创建" : "纪念日已更新");
          }}
          onError={(m) => setError(m)}
        />
      )}
    </>
  );
}

function AnniversaryItem({
  anniversary,
  meId,
  partner,
  expanded,
  onToggle,
  onEdit,
}: {
  anniversary: AnniversaryView;
  meId: string;
  partner: string;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
}) {
  const badge = monthDayBadge(anniversary.date);
  const countdown = anniversaryCountdown(anniversary.date);
  const mine = anniversary.createdBy === meId;
  const entriesQuery = useAnniversaryEntries(expanded ? anniversary.id : undefined);

  return (
    <div className="stack-sm">
      <button
        type="button"
        className="anni-item"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span
          className={
            anniversary.visibility === "shared" ? "anni-date pink" : "anni-date pur"
          }
        >
          <span className="d">{badge.d}</span>
          <span className="m">{badge.m}月</span>
        </span>
        <span className="anni-info">
          <span className="t">{anniversary.title}</span>
          <span className="d">
            {formatAnniversaryDate(anniversary.date)} ·{" "}
            {anniversary.visibility === "shared"
              ? `共享给 ${partner}`
              : "仅我可见，TA 看不到"}
            {anniversary.remindEnabled ? " · 当天提醒" : ""}
          </span>
        </span>
        <span className="countdown">{countdownLabel(countdown)}</span>
      </button>

      {expanded && (
        <div className="card stack-sm">
          <div className="row-between">
            <span className="section-title">那天的日记</span>
            {mine && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={onEdit}
              >
                编辑
              </button>
            )}
          </div>
          {entriesQuery.isLoading && (
            <div className="small ghost-text">看看那天…</div>
          )}
          {entriesQuery.isError && (
            <div className="small ghost-text">
              这条纪念日读不到了。
            </div>
          )}
          {entriesQuery.data !== undefined &&
            entriesQuery.data.entries.length === 0 && (
              <div className="small ghost-text">
                那天没有共享的日记。写下点什么，以后就能从这里跳回去看。
              </div>
            )}
          {entriesQuery.data?.entries.map((e) => (
            <Link key={e.id} to={`/entries/${e.id}`} className="setting-row">
              <span className="set-ico sky">
                <CalendarIcon size={16} />
              </span>
              <span className="set-txt">
                <span className="t">{e.title.trim() === "" ? "（无标题）" : e.title}</span>
                <span className="d mono">
                  {e.authorUsername} · {formatTime(e.writtenAt)}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AnniversaryForm({
  anniversary,
  partner,
  error,
  onClose,
  onSaved,
  onError,
}: {
  anniversary: AnniversaryView | null;
  partner: string;
  error: string | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const isEdit = anniversary !== null;
  const [title, setTitle] = useState(
    anniversary?.title ?? ANNIVERSARY_PRESET_TITLE,
  );
  const [date, setDate] = useState(anniversary?.date ?? "");
  const [visibility, setVisibility] = useState<Visibility>(
    anniversary?.visibility ?? "shared",
  );
  const [remindEnabled, setRemindEnabled] = useState(
    anniversary?.remindEnabled ?? true,
  );

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return api.updateAnniversary(anniversary.id, {
          title,
          date,
          visibility,
          remind_enabled: remindEnabled,
        });
      }
      return api.createAnniversary({
        title,
        date,
        visibility,
        remind_enabled: remindEnabled,
      });
    },
    onSuccess: onSaved,
    onError: (e) => onError(errorMessage(e)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (title.trim() === "" || date === "") {
      onError("给这条纪念日起个名字、选一个日期。");
      return;
    }
    save.mutate();
  }

  return (
    <Modal
      title={isEdit ? "编辑纪念日" : "新增纪念日"}
      onClose={onClose}
      closeOnMask={!save.isPending}
    >
      <form className="stack" onSubmit={onSubmit}>
        {error !== null && <div className="banner banner-error">{error}</div>}
        <label className="field-block">
          <span className="field-label">名称</span>
          <span className="field">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={ANNIVERSARY_PRESET_TITLE}
              maxLength={100}
              autoFocus
            />
          </span>
        </label>

        <label className="field-block">
          <span className="field-label">日期</span>
          <span className="field">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </span>
          <span className="field-hint">
            存储时带年份，展示时是否显示年份由你们自己理解 —— 产品不判断类型。
          </span>
        </label>

        <div className="field-block">
          <span className="field-label">谁能看到</span>
          <div className="chip-row">
            <button
              type="button"
              className={visibility === "shared" ? "chip on" : "chip"}
              onClick={() => setVisibility("shared")}
            >
              共享给 {partner}
            </button>
            <button
              type="button"
              className={visibility === "private" ? "chip on" : "chip"}
              onClick={() => setVisibility("private")}
            >
              仅我可见
            </button>
          </div>
          {visibility === "private" && (
            <span className="field-hint">
              仅我可见的纪念日对 {partner} 完全不存在 —— 不显示、不计入倒计时、
              不提醒。
            </span>
          )}
        </div>

        <div className="switch-row off">
          <span className="lb">当天在我的应用内提醒</span>
          <button
            type="button"
            role="switch"
            aria-checked={remindEnabled}
            aria-label="应用内提醒"
            className={remindEnabled ? "switch on" : "switch"}
            onClick={() => setRemindEnabled((v) => !v)}
          >
            <span className="knob" />
          </button>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={save.isPending}
          >
            {save.isPending ? "保存中…" : isEdit ? "保存" : "创建"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
